// Sprint 19 A2: Deal Risk Radar — dashboard ranked by risk × value
import {
  AlertTriangle, ShieldAlert, TrendingDown, Phone,
  MessageSquare, Mail, Brain, ChevronDown,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { useRiskRadar } from "@/hooks/use-crm-risk-radar";
import type { DealRiskAssessment } from "@/hooks/use-crm-risk-radar";
import { formatCurrency } from "@/lib/format";
import { SkeletonCard } from "@/components/skeleton";

const RISK_COLORS: Record<DealRiskAssessment["riskLevel"], { bg: string; text: string; border: string }> = {
  critical: { bg: "bg-[var(--color-destructive)]/10", text: "text-[var(--color-destructive)]", border: "border-l-[var(--color-destructive)]" },
  high: { bg: "bg-[var(--color-accent-action-light)]", text: "text-[var(--color-accent-action)]", border: "border-l-[var(--color-accent-action)]" },
  medium: { bg: "bg-[var(--color-warning)]/10", text: "text-[var(--color-warning)]", border: "border-l-[var(--color-warning)]" },
  low: { bg: "bg-[var(--color-surface)]", text: "text-[var(--color-muted-foreground)]", border: "border-l-[var(--color-border)]" },
};

export default function RiskRadarPage() {
  const { data, isLoading, error, refetch } = useRiskRadar();

  const sorted = [...(data ?? [])].sort((a, b) => b.priorityScore - a.priorityScore);

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[var(--color-foreground)]">Deal Risk Radar</h1>
        <p className="text-sm text-[var(--color-muted-foreground)] mt-1">
          Deals ranked by risk score × value. Highest-impact risks first.
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)]">
          {error} <button type="button" onClick={refetch} className="underline ml-2">Retry</button>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <SkeletonCard key={i} className="h-32 rounded-lg" />
          ))}
        </div>
      ) : sorted.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-[var(--color-border)] rounded-lg">
          <ShieldAlert className="h-12 w-12 mx-auto text-[var(--color-success)] mb-3" />
          <p className="text-sm font-medium text-[var(--color-foreground)]">No at-risk deals</p>
          <p className="text-xs text-[var(--color-muted-foreground)]">All deals are progressing normally</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sorted.map((deal, idx) => (
            <RiskCard key={deal.dealId} deal={deal} rank={idx + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

function RiskCard({ deal, rank }: { deal: DealRiskAssessment; rank: number }) {
  const [expanded, setExpanded] = useState(false);
  const colors = RISK_COLORS[deal.riskLevel];

  return (
    <div className={`stagger-item rounded-lg border border-[var(--color-border)] border-l-4 ${colors.border} bg-[var(--color-card)] card-hover overflow-hidden shadow-sm`}>
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-xs font-bold text-[var(--color-muted-foreground)] w-6">#{rank}</span>
            <div>
              <Link to={`/crm/deals/${deal.dealId}`} className="text-sm font-semibold text-[var(--color-foreground)] hover:text-[var(--color-primary)]">
                {deal.dealTitle}
              </Link>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs text-[var(--color-foreground)] font-medium">{formatCurrency(deal.valueCents)}</span>
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${colors.bg} ${colors.text}`}>
                  {deal.riskScore}% risk
                </span>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="p-1 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] btn-press"
            aria-expanded={expanded}
            aria-label={expanded ? "Collapse" : "Expand risk details"}
          >
            <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} style={{ transitionDuration: "var(--duration-normal)" }} />
          </button>
        </div>

        {/* Risk factors summary */}
        <div className="flex flex-wrap gap-1.5 mt-2">
          {deal.factors.map((f) => (
            <span key={f.factor} className={`text-[9px] px-1.5 py-0.5 rounded ${
              f.severity === "critical" ? "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]" :
              f.severity === "high" ? "bg-[var(--color-accent-action-light)] text-[var(--color-accent-action)]" :
              "bg-[var(--color-surface)] text-[var(--color-muted-foreground)]"
            }`}>
              <AlertTriangle className="h-2 w-2 inline mr-0.5" /> {f.description}
            </span>
          ))}
        </div>
      </div>

      {/* Expanded: recommendations */}
      {expanded && (
        <div className="border-t border-[var(--color-border)] p-4 bg-[var(--color-surface)] stagger-in">
          <h4 className="text-xs font-semibold text-[var(--color-ai)] mb-2 flex items-center gap-1">
            <Brain className="h-3 w-3" /> Recovery Recommendations
          </h4>
          <div className="space-y-2">
            {deal.recommendations.map((rec, i) => (
              <div key={i} className="flex items-start gap-2 text-xs">
                <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded flex-shrink-0 ${
                  rec.urgency === "immediate" ? "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]" :
                  "bg-[var(--color-warning)]/10 text-[var(--color-warning)]"
                }`}>{rec.urgency}</span>
                <div>
                  <p className="font-medium text-[var(--color-foreground)]">{rec.action}</p>
                  <p className="text-[var(--color-muted-foreground)] mt-0.5">{rec.reasoning}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
